import React, { useState, useEffect } from 'react';
import { businessModerationApi, BusinessModerationItem, ApproveBusinessProfileRequest } from '../services/api';
import { toast } from 'react-hot-toast';
import { 
  MagnifyingGlassIcon, 
  FunnelIcon, 
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  BuildingStorefrontIcon,
  ClockIcon,
  UserIcon,
  CalendarIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';

const AdminBusinessModerationPage: React.FC = () => {
  const [pendingProfiles, setPendingProfiles] = useState<BusinessModerationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProfile, setSelectedProfile] = useState<BusinessModerationItem | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [profileToReject, setProfileToReject] = useState<string | null>(null);

  useEffect(() => {
    fetchPendingProfiles();
  }, []);

  const fetchPendingProfiles = async () => {
    try {
      setLoading(true);
      const response = await businessModerationApi.getPendingProfiles();
      setPendingProfiles(response.data);
    } catch (error) {
      console.error('Error fetching pending profiles:', error);
      toast.error('خطا در بارگذاری پروفایل‌های در انتظار تایید');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveProfile = async (userId: string) => {
    setActionLoading(`approve-${userId}`);
    try {
      const request: ApproveBusinessProfileRequest = {
        userId,
        isApproved: true
      };
      
      await businessModerationApi.approveProfile(request);
      
      toast.success('کسب‌وکار با موفقیت تایید شد', {
        icon: '✅',
        style: {
          background: '#10B981',
          color: '#ffffff',
          fontFamily: 'Arial, sans-serif',
          textAlign: 'right',
          direction: 'rtl',
          borderRadius: '12px',
          fontSize: '14px',
          fontWeight: '500',
          padding: '16px',
          boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
        }
      });
      
      fetchPendingProfiles();
    } catch (error) {
      console.error('❌ Error approving profile:', error);
      toast.error('خطا در تایید کسب‌وکار', {
        icon: '❌',
        style: {
          background: '#EF4444',
          color: '#ffffff',
          fontFamily: 'Arial, sans-serif',
          textAlign: 'right',
          direction: 'rtl',
          borderRadius: '12px',
          fontSize: '14px',
          fontWeight: '500',
          padding: '16px',
          boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
        }
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectProfile = async () => {
    if (!profileToReject || !rejectionReason.trim()) {
      toast.error('لطفاً دلیل رد را وارد کنید');
      return;
    }

    setActionLoading(`reject-${profileToReject}`);
    try {
      const request: ApproveBusinessProfileRequest = {
        userId: profileToReject,
        isApproved: false,
        rejectionReason: rejectionReason.trim()
      };
      
      await businessModerationApi.approveProfile(request);
      
      toast.success('کسب‌وکار رد شد', {
        icon: '🚫',
        style: {
          background: '#EF4444',
          color: '#ffffff',
          fontFamily: 'Arial, sans-serif',
          textAlign: 'right',
          direction: 'rtl',
          borderRadius: '12px',
          fontSize: '14px',
          fontWeight: '500',
          padding: '16px',
          boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
        }
      });
      
      setShowRejectionModal(false);
      setRejectionReason('');
      setProfileToReject(null);
      fetchPendingProfiles();
    } catch (error) {
      console.error('❌ Error rejecting profile:', error);
      toast.error('خطا در رد فروشگاه', {
        icon: '❌',
        style: {
          background: '#EF4444',
          color: '#ffffff',
          fontFamily: 'Arial, sans-serif',
          textAlign: 'right',
          direction: 'rtl',
          borderRadius: '12px',
          fontSize: '14px',
          fontWeight: '500',
          padding: '16px',
          boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
        }
      });
    } finally {
      setActionLoading(null);
    }
  };

  const openRejectionModal = (userId: string) => {
    setProfileToReject(userId);
    setRejectionReason('');
    setShowRejectionModal(true);
  };

  const handleViewDetails = (profile: BusinessModerationItem) => {
    setSelectedProfile(profile);
    setShowDetailModal(true);
  };

  const filteredProfiles = pendingProfiles.filter(profile =>
    profile.businessName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatPendingChanges = (pendingChanges: string | undefined) => {
    if (!pendingChanges) return null;
    
    try {
      return JSON.parse(pendingChanges);
    } catch {
      return null;
    }
  };

  const formatBusinessHours = (hoursString: string | undefined) => {
    if (!hoursString) return 'ساعات کاری تنظیم نشده است';
    
    try {
      const hours = JSON.parse(hoursString);
      const dayNames: { [key: string]: string } = {
        'monday': 'دوشنبه',
        'tuesday': 'سه‌شنبه', 
        'wednesday': 'چهارشنبه',
        'thursday': 'پنج‌شنبه',
        'friday': 'جمعه',
        'saturday': 'شنبه',
        'sunday': 'یکشنبه'
      };

      return Object.entries(hours).map(([day, schedule]: [string, any]) => {
        const dayName = dayNames[day] || day;
        if (schedule.closed) {
          return `${dayName}: تعطیل`;
        }
        return `${dayName}: ${schedule.open} - ${schedule.close}`;
      }).join('\n');
    } catch {
      return hoursString;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" dir="rtl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BuildingStorefrontIcon className="w-8 h-8 text-violet-600" />
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">تایید فروشگاه‌ها</h1>
            <p className="text-sm text-gray-600">بررسی و تایید تغییرات اطلاعات فروشگاه</p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-xl border border-gray-200 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <ClockIcon className="w-6 h-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-gray-900">{pendingProfiles.length}</p>
              <p className="text-sm text-gray-600">در انتظار تایید</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-xl border border-gray-200 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <DocumentTextIcon className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-gray-900">
                {pendingProfiles.filter(p => p.pendingChanges).length}
              </p>
              <p className="text-sm text-gray-600">تغییرات پروفایل</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-xl border border-gray-200 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <BuildingStorefrontIcon className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-gray-900">
                {pendingProfiles.filter(p => !p.pendingChanges).length}
              </p>
              <p className="text-sm text-gray-600">فروشگاه جدید</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
          <div className="relative flex-1 lg:w-80">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="جستجوی فروشگاه‌ها..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Pending Profiles Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">فروشگاه</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">نوع درخواست</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">تاریخ ارسال</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">وضعیت</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">عملیات</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredProfiles.map((profile) => (
                <tr key={profile.userId} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                        <BuildingStorefrontIcon className="w-5 h-5 text-violet-600" />
                      </div>
                      <div className="mr-4">
                        <div className="text-sm font-medium text-gray-900">{profile.businessName}</div>
                        <div className="text-sm text-gray-500">شناسه: {profile.userId}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      profile.pendingChanges 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {profile.pendingChanges ? 'به‌روزرسانی اطلاعات' : 'فروشگاه جدید'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {profile.submittedAt 
                      ? new Date(profile.submittedAt).toLocaleDateString('fa-IR')
                      : 'نامشخص'
                    }
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      در انتظار تایید
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center gap-2 gap-2-reverse">
                      <button
                        onClick={() => handleViewDetails(profile)}
                        className="text-violet-600 hover:text-violet-900 p-1 rounded-md hover:bg-violet-50"
                        title="مشاهده جزئیات"
                      >
                        <EyeIcon className="w-4 h-4" />
                      </button>
                      
                      <button
                        onClick={() => handleApproveProfile(profile.userId)}
                        disabled={actionLoading === `approve-${profile.userId}`}
                        className="text-green-600 hover:text-green-900 p-1 rounded-md hover:bg-green-50 disabled:opacity-50"
                        title="تایید"
                      >
                        {actionLoading === `approve-${profile.userId}` ? (
                          <div className="w-4 h-4 border-2 border-green-600 border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <CheckCircleIcon className="w-4 h-4" />
                        )}
                      </button>
                      
                      <button
                        onClick={() => openRejectionModal(profile.userId)}
                        disabled={actionLoading === `reject-${profile.userId}`}
                        className="text-red-600 hover:text-red-900 p-1 rounded-md hover:bg-red-50 disabled:opacity-50"
                        title="رد"
                      >
                        {actionLoading === `reject-${profile.userId}` ? (
                          <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <XCircleIcon className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {filteredProfiles.length === 0 && (
          <div className="text-center py-12">
            <BuildingStorefrontIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No Stores Pending Approval</h3>
            <p className="mt-1 text-sm text-gray-500">All store requests have been reviewed.</p>
          </div>
        )}
      </div>

      {/* Profile Detail Modal */}
      {showDetailModal && selectedProfile && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Store Details</h3>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">General Information</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Store Name:</span>
                      <p className="font-medium">{selectedProfile.businessName}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">User ID:</span>
                      <p className="font-medium">{selectedProfile.userId}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Submission Date:</span>
                      <p className="font-medium">
                        {selectedProfile.submittedAt 
                          ? new Date(selectedProfile.submittedAt).toLocaleDateString('en-US')
                          : 'Unknown'
                        }
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-500">Status:</span>
                      <p className="font-medium">Pending Approval</p>
                    </div>
                  </div>
                </div>

                {selectedProfile.pendingChanges && (
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-2">تغییرات درخواستی</h4>
                    <div className="text-sm space-y-2">
                      {(() => {
                        const changes = formatPendingChanges(selectedProfile.pendingChanges);
                        if (!changes) return <p className="text-gray-500">اطلاعات مربوط به تغییرات درخواستی در دسترس نیست</p>;
                        
                        return Object.entries(changes).map(([key, value]) => {
                          if (key === 'SubmittedAt') return null;
                          
                          const fieldNames: { [key: string]: string } = {
                            'BusinessName': 'نام فروشگاه',
                            'BusinessDescription': 'توضیحات فروشگاه',
                            'BusinessWebsite': 'وب‌سایت',
                            'BusinessAddress': 'آدرس',
                            'BusinessHours': 'ساعات کاری'
                          };
                          
                          return (
                            <div key={key} className="border-l-2 border-blue-200 pl-3">
                              <span className="text-gray-600">{fieldNames[key] || key}:</span>
                              {key === 'BusinessHours' ? (
                                <div className="font-medium text-gray-900 whitespace-pre-line mt-1">
                                  {formatBusinessHours(value as string)}
                                </div>
                              ) : (
                                <p className="font-medium text-gray-900">{value as string || 'خالی'}</p>
                              )}
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                )}

                {selectedProfile.pendingImages && selectedProfile.pendingImages.length > 0 && (
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-3">گالری فروشگاه ({selectedProfile.pendingImages.length} تصویر)</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {selectedProfile.pendingImages.map((image, index) => (
                        <div key={image.id} className="relative group">
                          <img
                            src={image.imageUrl}
                            alt={image.imageDescription || `تصویر ${index + 1}`}
                            className="w-full h-24 object-cover rounded-lg border border-gray-200"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = '/placeholder-image.png';
                            }}
                          />
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 rounded-lg flex items-center justify-center">
                            <span className="text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-center px-2">
                              {image.imageDescription || 'بدون توضیح'}
                            </span>
                          </div>
                          <div className={`absolute top-1 left-1 px-1.5 py-0.5 rounded text-xs font-medium ${
                            image.moderationStatus === 0 ? 'bg-yellow-100 text-yellow-800' :
                            image.moderationStatus === 1 ? 'bg-green-100 text-green-800' :
                            image.moderationStatus === 2 ? 'bg-red-100 text-red-800' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                            {image.moderationStatus === 0 ? 'در انتظار' :
                             image.moderationStatus === 1 ? 'تایید شده' :
                             image.moderationStatus === 2 ? 'رد شده' : 'بررسی شده'}
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-gray-600 mt-2">
                      💡 تصاویر در انتظار: {selectedProfile.pendingImages.filter(img => img.moderationStatus === 0).length}
                    </p>
                  </div>
                )}

                {/* Show message when no images exist */}
                {(!selectedProfile.pendingImages || selectedProfile.pendingImages.length === 0) && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-2">گالری فروشگاه</h4>
                    <div className="text-center py-6">
                      <BuildingStorefrontIcon className="mx-auto h-12 w-12 text-gray-400" />
                      <p className="text-sm text-gray-500 mt-2">هنوز تصویری برای این فروشگاه آپلود نشده است.</p>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="flex justify-end gap-3 gap-3-reverse mt-6">
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  بستن
                </button>
                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    openRejectionModal(selectedProfile.userId);
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700"
                >
                  رد
                </button>
                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    handleApproveProfile(selectedProfile.userId);
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700"
                >
                  تایید
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Modal */}
      {showRejectionModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" dir="rtl">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-1/2 lg:w-1/3 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">رد فروشگاه</h3>
                <button
                  onClick={() => setShowRejectionModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    دلیل رد (الزامی)
                  </label>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="لطفاً دلیل رد فروشگاه را وارد کنید..."
                  />
                </div>
              </div>
              
              <div className="flex justify-end gap-3 gap-3-reverse mt-6">
                <button
                  onClick={() => setShowRejectionModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  لغو
                </button>
                <button
                  onClick={handleRejectProfile}
                  disabled={!rejectionReason.trim() || actionLoading?.startsWith('reject-')}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {actionLoading?.startsWith('reject-') ? 'در حال رد...' : 'رد فروشگاه'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBusinessModerationPage; 